import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import { FiArrowLeft, FiUsers, FiPlus, FiX, FiUserPlus, FiCheckCircle, FiClock } from 'react-icons/fi';

const TeamDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [team, setTeam] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddMember, setShowAddMember] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [userRoles, setUserRoles] = useState({});
  const [addingMembers, setAddingMembers] = useState(false);

  useEffect(() => {
    fetchTeam();
    fetchTasks();
    if (user?.role === 'teamLead' || user?.role === 'admin' || user?.role === 'productManager') {
      fetchUsers();
    }
  }, [id]);

  const fetchTeam = async () => {
    try {
      const response = await axios.get(`/teams/${id}`);
      setTeam(response.data);
    } catch (error) {
      console.error('Error fetching team:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTasks = async () => {
    try {
      const response = await axios.get('/tasks');
      const teamTasks = response.data.filter(
        task => task.team?._id === id || task.team === id
      );
      setTasks(teamTasks);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get('/users');
      // Filter out users already in team
      const existingMemberIds = team?.members?.map(m => m.user?._id || m.user) || [];
      const availableUsers = response.data.filter(
        u => !existingMemberIds.includes(u._id) && u._id !== team?.teamLead?._id
      );
      setAllUsers(availableUsers);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  useEffect(() => {
    if (team && (user?.role === 'team lead' || user?.role === 'teamlead' || user?.role === 'admin' || user?.role === 'product manager')) {
      fetchUsers();
    }
  }, [team]);

  const handleAddMembers = async () => {
    if (selectedUsers.length === 0) return;

    setAddingMembers(true);
    try {
      const members = selectedUsers.map(userId => ({
        userId,
        role: userRoles[userId] || 'member',
      }));

      await axios.post(`/teams/${id}/members`, { members });
      await fetchTeam();
      setSelectedUsers([]);
      setUserRoles({});
      setShowAddMember(false);
      fetchUsers();
    } catch (error) {
      console.error('Error adding members:', error);
      alert(error.response?.data?.message || 'Failed to add members');
    } finally {
      setAddingMembers(false);
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!window.confirm('Remove this member from the team?')) return;

    try {
      await axios.delete(`/teams/${id}/members/${memberId}`);
      await fetchTeam();
      fetchUsers();
    } catch (error) {
      console.error('Error removing member:', error);
      alert(error.response?.data?.message || 'Failed to remove member');
    }
  };

  const toggleUserSelection = (userId) => {
    if (selectedUsers.includes(userId)) {
      setSelectedUsers(selectedUsers.filter(id => id !== userId));
    } else {
      setSelectedUsers([...selectedUsers, userId]);
      if (!userRoles[userId]) {
        setUserRoles({ ...userRoles, [userId]: 'member' });
      }
    }
  };

  const canManageTeam = user?.role === 'admin' || 
    user?.role === 'teamLead' ||
    team?.teamLead?._id === user?._id ||
    team?.teamLead === user?._id;

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-300"></div>
        </div>
      </>
    );
  }

  if (!team) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-white mb-4">Team not found</h1>
            <Link to="/teams" className="text-purple-200 hover:text-purple-100">
              Go back to teams
            </Link>
          </div>
        </div>
      </>
    );
  }

  const teamTasks = tasks.filter(t => t.isTeamTask);
  const subtasks = tasks.filter(t => t.parentTask && !t.isTeamTask);

  return (
    <div className="min-h-screen bg-background relative">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative z-10">
        <Link
          to="/teams"
          className="inline-flex items-center space-x-2 text-dark-100 hover:text-purple-100 mb-6 transition-colors"
        >
          <FiArrowLeft />
          <span>Back to Teams</span>
        </Link>

        {/* Team Header */}
        <div className="bg-stroke border border-stroke rounded-lg p-6 mb-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">{team.name}</h1>
              {team.description && (
                <p className="text-dark-100">{team.description}</p>
              )}
            </div>
            {canManageTeam && (
              <Link
                to={`/create-task?team=${id}&isTeamTask=true`}
                className="flex items-center space-x-2 bg-gradient-to-r from-purple-200 to-purple-300 text-white px-4 py-2 rounded-lg font-semibold hover:opacity-90 transition-opacity"
              >
                <FiPlus />
                <span>Create Team Task</span>
              </Link>
            )}
          </div>
          <div className="flex items-center text-dark-100 text-sm">
            <FiUsers className="mr-2" />
            <span>Team Lead: {team.teamLead?.name || team.teamLead}</span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Team Members */}
          <div className="bg-stroke border border-stroke rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-white flex items-center">
                <FiUsers className="mr-2" />
                Team Members ({team.members?.length || 0})
              </h2>
              {canManageTeam && (
                <button
                  onClick={() => setShowAddMember(!showAddMember)}
                  className="flex items-center space-x-2 text-purple-200 hover:text-purple-100 transition-colors"
                >
                  <FiUserPlus />
                  <span>Add Members</span>
                </button>
              )}
            </div>

            {/* Add Members Form */}
            {showAddMember && canManageTeam && (
              <div className="bg-background border border-dark-300 rounded-lg p-4 mb-4">
                <h3 className="text-white font-medium mb-3">Select Members to Add</h3>
                <div className="max-h-64 overflow-y-auto space-y-2 mb-4">
                  {allUsers.map((userItem) => (
                    <div
                      key={userItem._id}
                      className="flex items-center justify-between p-2 bg-dark-300 rounded-lg"
                    >
                      <div className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          checked={selectedUsers.includes(userItem._id)}
                          onChange={() => toggleUserSelection(userItem._id)}
                          className="w-4 h-4 text-purple-300 rounded focus:ring-purple-300"
                        />
                        <div>
                          <div className="text-white text-sm font-medium">{userItem.name}</div>
                          <div className="text-dark-100 text-xs">{userItem.email}</div>
                        </div>
                      </div>
                      {selectedUsers.includes(userItem._id) && (
                        <select
                          value={userRoles[userItem._id] || 'member'}
                          onChange={(e) => setUserRoles({ ...userRoles, [userItem._id]: e.target.value })}
                          className="px-2 py-1 bg-background border border-dark-300 rounded text-white text-xs"
                        >
                          <option value="member">Member</option>
                          <option value="developer">Developer</option>
                          <option value="designer">Designer</option>
                          <option value="tester">Tester</option>
                          <option value="manager">Manager</option>
                        </select>
                      )}
                    </div>
                  ))}
                  {allUsers.length === 0 && (
                    <p className="text-dark-100 text-sm text-center py-4">
                      No available users to add
                    </p>
                  )}
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => {
                      setShowAddMember(false);
                      setSelectedUsers([]);
                      setUserRoles({});
                    }}
                    className="flex-1 px-4 py-2 bg-dark-300 text-white rounded-lg hover:bg-dark-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddMembers}
                    disabled={selectedUsers.length === 0 || addingMembers}
                    className="flex-1 px-4 py-2 bg-purple-300 text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {addingMembers ? 'Adding...' : `Add ${selectedUsers.length} Member(s)`}
                  </button>
                </div>
              </div>
            )}

            {/* Members List */}
            <div className="space-y-2">
              {team.members && team.members.length > 0 ? (
                team.members.map((member, index) => {
                  const memberUser = member.user || member;
                  return (
                    <div
                      key={memberUser._id || index}
                      className="flex items-center justify-between p-3 bg-background rounded-lg border border-dark-300"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 rounded-full bg-purple-300 flex items-center justify-center text-white font-semibold">
                          {memberUser.name?.charAt(0).toUpperCase() || 'M'}
                        </div>
                        <div>
                          <div className="text-white font-medium">{memberUser.name}</div>
                          <div className="text-dark-100 text-xs">{memberUser.email}</div>
                          {member.role && (
                            <span className="inline-block mt-1 px-2 py-0.5 bg-purple-300/20 text-purple-200 rounded text-xs">
                              {member.role}
                            </span>
                          )}
                        </div>
                      </div>
                      {canManageTeam && (
                        <button
                          onClick={() => handleRemoveMember(memberUser._id || memberUser)}
                          className="text-red-400 hover:text-red-300 transition-colors"
                        >
                          <FiX />
                        </button>
                      )}
                    </div>
                  );
                })
              ) : (
                <p className="text-dark-100 text-sm text-center py-4">
                  No members yet. Add members to get started.
                </p>
              )}
            </div>
          </div>

          {/* Team Tasks */}
          <div className="bg-stroke border border-stroke rounded-lg p-6">
            <h2 className="text-xl font-semibold text-white mb-4">Team Tasks</h2>
            
            {teamTasks.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-dark-100 mb-4">No team tasks yet</p>
                {canManageTeam && (
                  <Link
                    to={`/create-task?team=${id}&isTeamTask=true`}
                    className="inline-flex items-center space-x-2 bg-purple-300 text-white px-4 py-2 rounded-lg font-semibold hover:opacity-90 transition-opacity"
                  >
                    <FiPlus />
                    <span>Create Team Task</span>
                  </Link>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {teamTasks.map((task) => {
                  const taskSubtasks = subtasks.filter(st => st.parentTask?._id === task._id || st.parentTask === task._id);
                  const completedSubtasks = taskSubtasks.filter(st => st.status === 'completed').length;
                  const totalSubtasks = taskSubtasks.length;
                  
                  return (
                    <Link
                      key={task._id}
                      to={`/task/${task._id}`}
                      className="block p-4 bg-background border border-dark-300 rounded-lg hover:border-purple-300 transition-colors"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="text-white font-semibold flex-1">{task.title}</h3>
                        <span className={`px-2 py-1 rounded text-xs ${
                          task.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                          task.status === 'in-progress' ? 'bg-blue-500/20 text-blue-400' :
                          'bg-dark-300 text-dark-100'
                        }`}>
                          {task.status}
                        </span>
                      </div>
                      {totalSubtasks > 0 && (
                        <div className="flex items-center space-x-2 text-sm text-dark-100 mb-2">
                          <FiCheckCircle className="text-green-400" />
                          <span>{completedSubtasks}/{totalSubtasks} subtasks completed</span>
                        </div>
                      )}
                      <div className="w-full bg-dark-300 rounded-full h-2">
                        <div
                          className="bg-gradient-to-r from-purple-200 to-purple-300 h-2 rounded-full transition-all"
                          style={{ width: `${task.progress || 0}%` }}
                        />
                      </div>
                      <div className="text-xs text-dark-100 mt-1">{task.progress || 0}% complete</div>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamDetails;
